import { createContext, useContext, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { useConfig } from "../config/ConfigProvider";
import { createApiClient } from "./client";
import type { ApiClient } from "./client";

const ApiContext = createContext<ApiClient | null>(null);

/**
 * Creates a single API client wired to the current config and the live Cognito
 * ID token. The token is read through a ref so renewed tokens are picked up
 * without rebuilding the client (and without re-rendering consumers).
 */
export function ApiProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const config = useConfig();

  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = auth.user?.id_token;

  const client = useMemo(
    () => createApiClient(config.apiBaseUrl, () => tokenRef.current),
    [config.apiBaseUrl],
  );

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

/** Access the shared API client. Throws if used outside an ApiProvider. */
export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return client;
}
